import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isPeriod, periodOf } from '@entities/chronum';

export type ChronumTab = 'hours' | 'payroll' | 'positions' | 'sites' | 'history';

const TABS: readonly ChronumTab[] = ['hours', 'payroll', 'positions', 'sites', 'history'];

function isTab(value: string | null): value is ChronumTab {
  return value !== null && (TABS as readonly string[]).includes(value);
}

/** Сколько месяцев назад можно выбрать. */
export const PERIOD_DEPTH = 24;

export interface ChronumParams {
  tab: ChronumTab;
  /** Выбранный месяц `ГГГГ-ММ`: не позже текущего. */
  period: string;
  currentPeriod: string;
  /** Площадка, к которой перешли из аномалии: её привязку открыть сразу. */
  site: string | null;
  /** Должности: только без категории. */
  unmappedOnly: boolean;
  /** Площадки: только требующие внимания. */
  attentionOnly: boolean;
  setTab: (tab: ChronumTab) => void;
  setPeriod: (period: string) => void;
  setUnmappedOnly: (on: boolean) => void;
  setAttentionOnly: (on: boolean) => void;
  /** Перейти к площадке (из аномалии) — с записью в историю: «Назад» вернёт к часам. */
  openSite: (siteId: string) => void;
  /** Перейти к должностям без категории (из аномалии). */
  openUnmapped: () => void;
  /** Площадку открыли — убрать её из адреса. */
  clearSite: () => void;
}

/**
 * Состояние экрана в адресе: вкладка, месяц, фильтры и площадка из аномалии —
 * ссылку можно переслать, «Назад» возвращает туда, откуда пришли.
 */
export function useChronumParams(): ChronumParams {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const currentPeriod = periodOf(new Date());

  const rawPeriod = params.get('period');
  const period = isPeriod(rawPeriod) && rawPeriod <= currentPeriod ? rawPeriod : currentPeriod;
  const rawTab = params.get('tab');
  const tab: ChronumTab = isTab(rawTab) ? rawTab : 'hours';

  const update = useCallback(
    (patch: Record<string, string | null>, push = false) => {
      const next = new URLSearchParams(location.search);
      for (const [k, v] of Object.entries(patch)) {
        if (v === null) next.delete(k);
        else next.set(k, v);
      }
      const s = next.toString();
      navigate({ search: s ? `?${s}` : '' }, { replace: !push });
    },
    [location.search, navigate],
  );

  return {
    tab,
    period,
    currentPeriod,
    site: params.get('site'),
    unmappedOnly: params.get('filter') === 'unmapped',
    attentionOnly: params.get('filter') === 'attention',
    setTab: (next) => update({ tab: next === 'hours' ? null : next, filter: null, site: null }),
    setPeriod: (next) => update({ period: next === currentPeriod ? null : next }),
    setUnmappedOnly: (on) => update({ filter: on ? 'unmapped' : null }),
    setAttentionOnly: (on) => update({ filter: on ? 'attention' : null }),
    openSite: (siteId) => update({ tab: 'sites', filter: null, site: siteId }, true),
    openUnmapped: () => update({ tab: 'positions', filter: 'unmapped', site: null }, true),
    clearSite: () => update({ site: null }),
  };
}
